import {
  ACCESS,
  WORKFLOW_CFS_CODES,
  WORKFLOW_NEXT_CODES,
  WORKFLOW_LEGEND_STEPS,
  formatStepCodes,
  buildWorkflowCfsTitle,
  buildWorkflowNextTitle,
  buildRequestCodesTitle,
} from "@constants/permissionAccessMatrix";

const badgeBase =
  "inline-flex h-5 min-w-5 items-center justify-center rounded px-1.5 text-xs font-bold leading-none";

/** Soft amber bg for O / O,M / C·A·F·S·R chips (matches textWarning #FF9501). */
const WARNING_SOFT_BG = "bg-[#FF950133]";

const codeChipClass = (colors) =>
  `inline-flex items-center rounded px-1.5 py-0.5 text-xs font-semibold leading-none ${WARNING_SOFT_BG} ${colors.textWarning}`;

const CodeChips = ({ lines, title, colors, stacked = false }) => (
  <span
    className={`inline-flex ${stacked ? "flex-col items-start" : "flex-row flex-wrap items-center"} gap-1`}
    title={title}
    aria-label={title}
  >
    {lines.map((line) => (
      <span key={line} className={codeChipClass(colors)} aria-hidden="true">
        {line}
      </span>
    ))}
  </span>
);

const RequestCodeChip = ({ codes, t, colors }) => {
  const title = buildRequestCodesTitle(t, codes);
  return (
    <span className={codeChipClass(colors)} title={title} aria-label={title}>
      {formatStepCodes("R", codes)}
    </span>
  );
};

export const AccessCell = ({ access, t, colors }) => {
  if (access === ACCESS.YES) {
    const label = t("role.permission-matrix.access.yes");
    return (
      <span
        className={`${badgeBase} ${colors.bgIconAccent} ${colors.textActive}`}
        title={label}
        aria-label={label}
      >
        ✓
      </span>
    );
  }
  if (access === ACCESS.NO) {
    const label = t("role.permission-matrix.access.no");
    return (
      <span
        className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-[2px] border-2 ${colors.borderPrimary} bg-transparent`}
        title={label}
        aria-label={label}
        role="img"
      />
    );
  }
  if (access === ACCESS.OWNER) {
    const label = t("role.permission-matrix.access.owner");
    return (
      <span
        className={`${badgeBase} ${WARNING_SOFT_BG} ${colors.textWarning}`}
        title={label}
        aria-label={label}
      >
        O
      </span>
    );
  }
  if (access === ACCESS.OWNER_MEMBER) {
    const label = t("role.permission-matrix.access.owner_member");
    return (
      <span
        className={`${badgeBase} ${WARNING_SOFT_BG} ${colors.textWarning}`}
        title={label}
        aria-label={label}
      >
        O, M
      </span>
    );
  }
  if (access === ACCESS.LOWER_USER) {
    return (
      <span
        className={`text-xs font-semibold leading-snug ${colors.textPrimary}`}
        title={t("role.permission-matrix.access.lower_user")}
      >
        {t("role.permission-matrix.access.lower_user_short", "Lower user")}
      </span>
    );
  }
  if (access === ACCESS.WORKFLOW_CF_S) {
    return (
      <CodeChips
        colors={colors}
        stacked
        title={buildWorkflowCfsTitle(t)}
        lines={[
          formatStepCodes("C", WORKFLOW_CFS_CODES.clarity),
          formatStepCodes("A", WORKFLOW_CFS_CODES.clarity_air),
          formatStepCodes("F", WORKFLOW_CFS_CODES.fossid),
          formatStepCodes("S", WORKFLOW_CFS_CODES.sbom),
        ]}
      />
    );
  }
  if (access === ACCESS.WORKFLOW_NEXT) {
    return (
      <CodeChips
        colors={colors}
        stacked
        title={buildWorkflowNextTitle(t)}
        lines={[
          formatStepCodes("C", WORKFLOW_NEXT_CODES.clarity),
          formatStepCodes("A", WORKFLOW_NEXT_CODES.clarity_air),
          formatStepCodes("F", WORKFLOW_NEXT_CODES.fossid),
        ]}
      />
    );
  }
  if (access === ACCESS.REQUEST_R123) {
    return <RequestCodeChip codes={[1, 2, 3]} t={t} colors={colors} />;
  }
  if (access === ACCESS.WORKFLOW_REQUEST) {
    return <RequestCodeChip codes={[1, 2, 3, 4]} t={t} colors={colors} />;
  }

  const fullLabel = t(`role.permission-matrix.access.${access}`, access);
  return (
    <span className={`text-xs leading-snug ${colors.textPrimary} break-words`} title={fullLabel}>
      {fullLabel}
    </span>
  );
};

const LEGEND_BASIC_ITEMS = [
  { access: ACCESS.YES },
  { access: ACCESS.NO },
  { access: ACCESS.OWNER },
  { access: ACCESS.OWNER_MEMBER },
  { access: ACCESS.LOWER_USER },
];

const WORKFLOW_STEP_FAMILIES = [
  { family: "clarity", prefix: "C", accessKeys: [ACCESS.WORKFLOW_CF_S, ACCESS.WORKFLOW_NEXT] },
  { family: "clarity_air", prefix: "A", accessKeys: [ACCESS.WORKFLOW_CF_S, ACCESS.WORKFLOW_NEXT] },
  { family: "fossid", prefix: "F", accessKeys: [ACCESS.WORKFLOW_CF_S, ACCESS.WORKFLOW_NEXT] },
  { family: "sbom", prefix: "S", accessKeys: [ACCESS.WORKFLOW_CF_S] },
  {
    family: "request",
    prefix: "R",
    accessKeys: [ACCESS.REQUEST_R123, ACCESS.WORKFLOW_REQUEST],
  },
];

/** Column layout: Request under SBOM to balance height. */
const WORKFLOW_STEP_COLUMNS = [
  ["clarity", "fossid"],
  ["clarity_air", "sbom", "request"],
];

const WorkflowStepList = ({ family, prefix, steps, t, colors }) => (
  <div>
    <p className={`mb-1 text-[11px] font-semibold ${colors.textPrimary}`}>
      {t(`role.permission-matrix.steps.${family}-title`, family)}
    </p>
    <ul className={`m-0 space-y-0.5 p-0 list-none text-[11px] ${colors.textSecondary}`}>
      {steps.map((n) => (
        <li key={`${prefix}${n}`}>
          <span className={`font-semibold ${colors.textWarning}`}>
            {prefix}
            {n}
          </span>
          <span className="mx-1">·</span>
          <span>{t(`role.permission-matrix.steps.${family}.${n}`)}</span>
        </li>
      ))}
    </ul>
  </div>
);

/** Collect distinct access values shown in matrix groups / full-matrix rows. */
export const collectUsedAccess = (groups) => {
  const used = new Set();
  if (!groups?.length) return used;

  for (const { rows } of groups) {
    for (const row of rows) {
      if (row.access) used.add(row.access);
      if (row.cells) {
        Object.values(row.cells).forEach((access) => {
          if (access) used.add(access);
        });
      }
    }
  }
  return used;
};

/**
 * @param {Set<string>|string[]|null} usedAccess - when set, only explain symbols present in the table
 */
export const MatrixLegend = ({ colors, t, dataTestId, compact = false, usedAccess = null }) => {
  const used = usedAccess
    ? usedAccess instanceof Set
      ? usedAccess
      : new Set(usedAccess)
    : null;
  const isUsed = (access) => !used || used.has(access);

  const basicItems = LEGEND_BASIC_ITEMS.filter(({ access }) => isUsed(access));
  const stepFamilyById = Object.fromEntries(
    WORKFLOW_STEP_FAMILIES.filter(({ accessKeys }) => accessKeys.some((key) => isUsed(key))).map(
      (item) => [item.family, item],
    ),
  );
  const stepColumns = WORKFLOW_STEP_COLUMNS.map((ids) =>
    ids.map((id) => stepFamilyById[id]).filter(Boolean),
  ).filter((col) => col.length > 0);
  const hasStepFamilies = stepColumns.length > 0;

  if (!basicItems.length && !hasStepFamilies) return null;

  return (
    <div
      className={`rounded-lg border ${colors.borderPrimary} ${colors.bgInput} px-3 py-2.5 space-y-3`}
      data-testid={dataTestId}
    >
      {basicItems.length > 0 ? (
        <div>
          <p className={`mb-1.5 text-xs font-semibold uppercase tracking-wide ${colors.textSubTitle}`}>
            {t("role.permission-matrix.legend-title", "Legend")}
          </p>
          <ul
            className={`m-0 grid grid-cols-2 gap-x-4 gap-y-2 p-0 list-none text-xs ${colors.textSecondary}`}
          >
            {basicItems.map(({ access }) => (
              <li key={access} className="flex items-center gap-2">
                <AccessCell access={access} t={t} colors={colors} />
                <span>
                  {access === ACCESS.LOWER_USER
                    ? t("role.permission-matrix.access.lower_user")
                    : t(`role.permission-matrix.access.${access}`)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {hasStepFamilies ? (
        <div className={basicItems.length > 0 ? `border-t ${colors.borderPrimary} pt-3` : ""}>
          <p className={`mb-2 text-xs font-semibold uppercase tracking-wide ${colors.textSubTitle}`}>
            {t("role.permission-matrix.steps-title", "Workflow step codes")}
          </p>
          <div className={`grid gap-3 ${compact ? "sm:grid-cols-2" : "sm:grid-cols-2"}`}>
            {stepColumns.map((column, colIndex) => (
              <div key={colIndex} className="space-y-3">
                {column.map(({ family, prefix }) => (
                  <WorkflowStepList
                    key={family}
                    family={family}
                    prefix={prefix}
                    steps={WORKFLOW_LEGEND_STEPS[family]}
                    t={t}
                    colors={colors}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

/**
 * Scrollable permission matrix for one role/permission code.
 */
const PermissionMatrixSection = ({ groups, colors, t, dataTestId }) => {
  if (!groups?.length) return null;

  const usedAccess = collectUsedAccess(groups);

  return (
    <div className="space-y-4" data-testid={`${dataTestId}-matrix`}>
      <div className="flex items-center justify-between gap-2">
        <p className={`mb-0 text-xs font-semibold uppercase tracking-wide ${colors.textSubTitle}`}>
          {t("role.permission-matrix.title", "Access matrix")}
        </p>
      </div>

      <div className={`rounded-lg border ${colors.borderPrimary} overflow-hidden`}>
        <div className="max-h-[22rem] overflow-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                <th
                  className={`px-3 py-2 text-left font-semibold ${colors.textPrimary} w-[32%] sticky top-0 z-[2] ${colors.bgPrimaryDarker}`}
                >
                  {t("role.permission-matrix.col-module", "Module")}
                </th>
                <th
                  className={`px-3 py-2 text-left font-semibold ${colors.textPrimary} sticky top-0 z-[2] ${colors.bgPrimaryDarker}`}
                >
                  {t("role.permission-matrix.col-action", "Action")}
                </th>
                <th
                  className={`px-3 py-2 text-left font-semibold ${colors.textPrimary} w-[40%] sticky top-0 z-[2] ${colors.bgPrimaryDarker}`}
                >
                  {t("role.permission-matrix.col-access", "Access")}
                </th>
              </tr>
            </thead>
            <tbody>
              {groups.map(({ group, rows }) =>
                rows.map((row, rowIndex) => (
                  <tr
                    key={row.id}
                    className={`border-t ${colors.borderPrimary} ${rowIndex % 2 === 0 ? colors.bgInput : ""}`}
                    data-testid={`${dataTestId}-matrix-row-${row.id}`}
                  >
                    {rowIndex === 0 ? (
                      <td
                        rowSpan={rows.length}
                        className={`px-3 py-2 align-top font-medium ${colors.textActive} border-r ${colors.borderPrimary}`}
                      >
                        {t(`role.permission-matrix.groups.${group}`, group)}
                      </td>
                    ) : null}
                    <td className={`px-3 py-2 ${colors.textPrimary}`}>
                      {t(`role.permission-matrix.features.${row.feature}`, row.feature)}
                    </td>
                    <td className="px-3 py-2">
                      <AccessCell access={row.access} t={t} colors={colors} />
                    </td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
        </div>
      </div>

      <MatrixLegend
        colors={colors}
        t={t}
        usedAccess={usedAccess}
        dataTestId={`${dataTestId}-matrix-legend`}
      />
    </div>
  );
};

export default PermissionMatrixSection;
